
import { Core } from '@pdftron/webviewer';
import { PageBoxes } from './enums';
export class TextDataExtractor {
    private readonly pagesCache: { idx: number, boxes: { [type: number]: Core.PDFNet.Rect } }[] = [];

    private extractPagePromise: Promise<string> | null = null;
    private getPageBoxPromise: Promise<Core.PDFNet.Rect> | null = null;

    constructor(private readonly pdfNetUtil: any,
        private readonly pdfDoc: Core.PDFNet.PDFDoc) { }

    async getPageBox(idx: number, type: PageBoxes): Promise<Core.PDFNet.Rect> {
        if (this.getPageBoxPromise === null) {
            this.getPageBoxPromise = new Promise((resolve, reject) => {
                this.doGetPageBox(idx, type)
                    .then(x => {
                        this.getPageBoxPromise = null;
                        resolve(x);
                    })
                    .catch(err => {
                        this.getPageBoxPromise = null;
                        reject(err);
                    });
            });
        }

        return this.getPageBoxPromise;
    }

    async extractPageXml(idx: number): Promise<string> {
        let xml = await this.extractXML(idx, false).catch(err => Promise.reject(err));
        if (!this.hasPageText(xml)) {
            xml = await this.extractXML(idx, true).catch(err => Promise.reject(err));
        }

        return Promise.resolve(xml);
    }

    private extractXML(idx: number, findHiddenText: boolean): Promise<string> {
        if (this.extractPagePromise === null) {
            this.extractPagePromise = new Promise((resolve, reject) => {
                this.doExtractPageXml(idx, findHiddenText)
                    .then(x => {
                        this.extractPagePromise = null;
                        resolve(x);
                    })
                    .catch(err => {
                        this.extractPagePromise = null;
                        reject(err);
                    });
            });
        }

        return this.extractPagePromise;
    }

    private async doGetPageBox(idx: number, type: PageBoxes): Promise<Core.PDFNet.Rect> {
        const filtered = this.pagesCache.filter(x => x.idx === idx);

        if (filtered.length > 0) {
            if (filtered[0].boxes[type]) {
                return filtered[0].boxes[type];
            } else {
                const page = await this.pdfDoc.getPage(idx);
                filtered[0].boxes[type] = await page.getBox(type);

                return filtered[0].boxes[type];
            }
        } else {
            const page = await this.pdfDoc.getPage(idx);

            const boxes: { [type: number]: Core.PDFNet.Rect } = {};
            boxes[type] = await page.getBox(type);

            const newItem = { idx, boxes };
            this.pagesCache.push(newItem);

            return boxes[type];
        }
    }

    private async doExtractPageXml(idx: number, findHiddenText: boolean): Promise<string> {
        this.pdfNetUtil.startDeallocateStack();
        const txtExtractor = await this.pdfNetUtil.TextExtractor.create();
        const page = await this.pdfDoc.getPage(idx);

        const flags: number = findHiddenText ? this.pdfNetUtil.TextExtractor.ProcessingFlags.e_extract_using_zorder
                                             :  this.pdfNetUtil.TextExtractor.ProcessingFlags.e_remove_hidden_text
                                                // | this.pdfNetUtil.TextExtractor.ProcessingFlags.e_no_invisible_text
                                                // | this.pdfNetUtil.TextExtractor.ProcessingFlags.e_punct_break
                                                // | this.pdfNetUtil.TextExtractor.ProcessingFlags.e_no_watermarks
                                                // | this.pdfNetUtil.TextExtractor.ProcessingFlags.e_no_ligature_exp
                                                // | this.pdfNetUtil.TextExtractor.ProcessingFlags.e_no_dup_remove
                                                | this.pdfNetUtil.TextExtractor.ProcessingFlags.e_extract_using_zorder;

        await txtExtractor.begin(
            page,
            await this.getPageBox(idx, PageBoxes.Media),
            flags
        );

        const xml = await txtExtractor.getAsXML(
            this.pdfNetUtil.TextExtractor.XMLOutputFlags.e_words_as_elements |
            this.pdfNetUtil.TextExtractor.XMLOutputFlags.e_output_bbox |
            this.pdfNetUtil.TextExtractor.XMLOutputFlags.e_output_style_info
        );

        txtExtractor.destroy();
        this.pdfNetUtil.endDeallocateStack();

        return xml;
    }

    private hasPageText(xml: string): boolean {
        const parser = new DOMParser();
        const parsedData: XMLDocument = parser.parseFromString(xml, 'application/xml');
        return parsedData.getElementsByTagName('Word').length > 0;
    }
}
